import { faChartLine } from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import { formatCurrencyCentValue, formatRFCDate } from '@/utils/helpers';
import type { JournalCapitalLadderBucket } from '@/models';
import DataTable, { type DataTableColumn } from '@/components/ui/DataTable';
import EmptyState from '@/components/ui/EmptyState';
import ErrorState from '@/components/ui/ErrorState';
import StatCard from '@/components/ui/StatCard';
import { pnlColorClass } from '@/components/journal/journalHelpers';
import { useJournalResource } from '@/components/journal/useJournalResource';

export default function JournalCapitalLadderPage() {
  const { data: ladder, loading, error, reload } = useJournalResource(
    () => APIRoutes.getJournalCapitalLadder(),
    [],
    'Fehler beim Laden der Kapital-Leiter',
  );

  const buckets = ladder?.buckets ?? [];
  const maxCapital = buckets.reduce((m, b) => Math.max(m, b.capital), 0);

  const columns: DataTableColumn<JournalCapitalLadderBucket>[] = [
    {
      key: 'week',
      header: 'Woche',
      primary: true,
      render: (b) => <span className="font-medium text-gray-900 dark:text-white">{b.week}</span>,
    },
    {
      key: 'range',
      header: 'Zeitraum',
      render: (b) => <span className="text-gray-500 dark:text-gray-400">{formatRFCDate(b.from)} – {formatRFCDate(b.to)}</span>,
    },
    {
      key: 'symbols',
      header: 'Symbole',
      render: (b) => <span className="block max-w-[14rem] truncate" title={b.symbols.join(', ')}>{b.symbols.join(', ')}</span>,
    },
    {
      key: 'positionCount',
      header: 'Positionen',
      align: 'right',
      render: (b) => b.positionCount,
    },
    {
      key: 'capital',
      header: 'Kapital',
      align: 'right',
      render: (b) => (
        <div className="flex items-center justify-end gap-2">
          <div className="hidden md:block w-24 h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
            <div className="h-full bg-blue-500" style={{ width: maxCapital > 0 ? `${(b.capital / maxCapital) * 100}%` : '0%' }} />
          </div>
          <span className="font-medium text-gray-900 dark:text-white">{formatCurrencyCentValue(b.capital, 'USD')}</span>
        </div>
      ),
    },
    {
      key: 'netCredit',
      header: 'Credit',
      align: 'right',
      render: (b) => <span className={pnlColorClass(b.netCredit)}>{formatCurrencyCentValue(b.netCredit, 'USD')}</span>,
    },
    {
      key: 'cumulativeReleased',
      header: 'Kumuliert frei',
      align: 'right',
      render: (b) => <span className="text-gray-500 dark:text-gray-400">{formatCurrencyCentValue(b.cumulativeReleased, 'USD')}</span>,
    },
    {
      key: 'remainingCapital',
      header: 'Verbleibend',
      align: 'right',
      render: (b) => <span className="font-medium text-gray-900 dark:text-white">{formatCurrencyCentValue(b.remainingCapital, 'USD')}</span>,
    },
  ];

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Worst-Case gebundenes Kapital offener Short-Puts nach Verfallswoche, mit kumulierter Freisetzung.
      </p>

      {error !== null ? (
        <ErrorState message={error} onRetry={reload} />
      ) : (
        <>
          {ladder !== null && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <StatCard label="Gesamt gebundenes Kapital" value={formatCurrencyCentValue(ladder.totalBoundCapital, 'USD')} />
              <StatCard
                label="Erhaltener Credit"
                value={formatCurrencyCentValue(ladder.totalNetCredit, 'USD')}
                valueClassName={pnlColorClass(ladder.totalNetCredit)}
              />
              <StatCard label="Verfallswochen" value={`${ladder.bucketCount}`} />
            </div>
          )}

          <DataTable<JournalCapitalLadderBucket>
            columns={columns}
            rows={buckets}
            keyExtractor={(b) => b.week}
            loading={loading}
            emptyState={(
              <EmptyState
                icon={faChartLine}
                title="Keine offenen Short-Puts"
                description="Gebundenes Kapital erscheint hier, sobald Short-Puts offen sind – jede Verfallswoche wird dann als eigene Stufe der Leiter angezeigt."
              />
            )}
          />
        </>
      )}
    </div>
  );
}
